// Mirrored from mju-ai-workbench/shared/schemas/agentRegistry.ts — keep the two identical but for this line and the import extensions.
/**
 * The organisation's agent registry orders (docs/plans/2026-10-microsoft-agent-365-and-defender.md,
 * B3/B5/B6) and the state a machine reports back (docs/plans/2026-10-agent-365-aioe-contract.md,
 * "Registry and risk").
 *
 * The AIOE learns from Microsoft (Entra `accountEnabled`, ID Protection's risky agents, the Agent 365
 * registry) that a specialist's agent identity is disabled, blocked or at risk, and answers every
 * heartbeat with an order beside `update`:
 *
 *   heartbeat answer  { agents: FleetAgentCommand[] }
 *   heartbeat body    { agents: FleetAgentsReport }
 *
 * A machine acts on an order id once (a repeat of an id it has applied is a no-op), keeps the state
 * per specialist across restarts, and reports it on every heartbeat with how many sessions of that
 * specialist still run, so the AIOE stops repeating an order once the report shows it applied and,
 * for disable / quarantine, nothing running.
 *
 * Which agents an order names (`specialistId`): a specialist's id; a team member that references a
 * specialist (`ref`) is that specialist; a team role with no specialist behind it is
 * `<teamId>/<memberName>`. An order that carries `entraAgentId` also matches any member carrying that
 * id, whatever its key. Pure: no I/O, no clock unless passed in.
 */
import { z } from "zod";
export const fleetAgentActionSchema = z.enum(["disable", "quarantine", "enable"]);
/** One order on the heartbeat answer's `agents` list. */
export const fleetAgentCommandSchema = z.object({
    id: z.string().min(1).max(100),
    action: fleetAgentActionSchema,
    /** The specialist's id, or `<teamId>/<memberName>` for a team role (see the file header). */
    specialistId: z.string().min(1).max(200),
    /** The Entra agent identity the order is about, when the organisation has one. */
    entraAgentId: z.string().min(1).max(100).optional(),
    /** Shown to the person in words ("Blocked in the Microsoft 365 admin center", "High agent risk"). */
    reason: z.string().max(500).optional(),
    requestedBy: z.string().max(200).optional(),
    requestedAt: z.string().max(64).optional(),
});
/** At most this many orders on one answer are read; the rest wait for the next heartbeat. */
export const MAX_AGENT_COMMANDS = 50;
/** The `agents` of a heartbeat's answer: each order that parses, in order; anything else is dropped. */
export function parseFleetAgentCommands(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw.slice(0, MAX_AGENT_COMMANDS)) {
        const parsed = fleetAgentCommandSchema.safeParse(item);
        if (parsed.success)
            out.push(parsed.data);
    }
    return out;
}
export const agentRegistryStateSchema = z.enum(["disabled", "quarantined"]);
/** One specialist the organisation has stopped, as the machine keeps it. */
export const agentRegistryRecordSchema = z.object({
    specialistId: z.string().min(1).max(200),
    entraAgentId: z.string().min(1).max(100).optional(),
    state: agentRegistryStateSchema,
    reason: z.string().max(500).optional(),
    /** The order that set this state. */
    orderId: z.string().min(1).max(100),
    requestedBy: z.string().max(200).optional(),
    requestedAt: z.string().max(64).optional(),
    /** When this machine applied it (ISO). */
    appliedAt: z.string().max(64),
});
/** What a machine persists and hands each host: the stopped specialists and the order ids it has applied. */
export const agentRegistrySnapshotSchema = z.object({
    records: z.array(agentRegistryRecordSchema).max(500).default([]),
    /** Order ids applied, newest last, at most {@link MAX_APPLIED_ORDERS}. */
    applied: z.array(z.string().min(1).max(100)).max(200).default([]),
});
export const MAX_APPLIED_ORDERS = 100;
export const EMPTY_AGENT_REGISTRY = { records: [], applied: [] };
/** A snapshot read back from disk or a frame; anything unreadable is the empty one. */
export function readAgentRegistrySnapshot(raw) {
    const parsed = agentRegistrySnapshotSchema.safeParse(raw);
    return parsed.success ? parsed.data : { records: [], applied: [] };
}
/**
 * Apply the orders of one heartbeat answer, in the order given. An order whose id was applied
 * before changes nothing. `enable` lifts the state; `disable` never softens a quarantine (only
 * `enable` lifts that). Returns the new snapshot and what changed.
 */
export function applyAgentCommands(snapshot, commands, now) {
    const records = new Map(snapshot.records.map((r) => [r.specialistId, r]));
    const applied = [...snapshot.applied];
    const seen = new Set(applied);
    const changes = [];
    for (const command of commands) {
        if (seen.has(command.id))
            continue;
        seen.add(command.id);
        applied.push(command.id);
        const current = records.get(command.specialistId);
        const previous = current?.state;
        if (command.action === "enable") {
            if (current) {
                records.delete(command.specialistId);
                changes.push({ command, specialistId: command.specialistId, previous });
            }
            continue;
        }
        const next = command.action === "quarantine" || previous === "quarantined" ? "quarantined" : "disabled";
        records.set(command.specialistId, {
            specialistId: command.specialistId,
            ...(command.entraAgentId ?? current?.entraAgentId ? { entraAgentId: command.entraAgentId ?? current?.entraAgentId } : {}),
            state: next,
            ...(command.reason ? { reason: command.reason } : current?.reason ? { reason: current.reason } : {}),
            orderId: command.id,
            ...(command.requestedBy ? { requestedBy: command.requestedBy } : {}),
            ...(command.requestedAt ? { requestedAt: command.requestedAt } : {}),
            appliedAt: now.toISOString(),
        });
        if (previous !== next)
            changes.push({ command, specialistId: command.specialistId, previous, next });
    }
    return { snapshot: { records: [...records.values()], applied: applied.slice(-MAX_APPLIED_ORDERS) }, changes };
}
/** The keys a team member answers to: the specialist it is, else its team role. */
export function registryKeysFor(team, member) {
    const keys = [];
    if (member.ref)
        keys.push(member.ref);
    if (team.kind === "specialist")
        keys.push(team.id);
    keys.push(`${team.id}/${member.name}`);
    return [...new Set(keys)];
}
/** The record that stops this member, or undefined when it may run. */
export function registryBlockFor(records, keys, entraAgentId) {
    if (!records.length)
        return undefined;
    const byKey = records.find((r) => keys.includes(r.specialistId));
    if (byKey)
        return byKey;
    return entraAgentId ? records.find((r) => r.entraAgentId && r.entraAgentId.toLowerCase() === entraAgentId.toLowerCase()) : undefined;
}
/** The sentence a person reads: "Quarantined by your organisation: High agent risk." */
export function describeRegistryBlock(record) {
    const what = record.state === "quarantined" ? "Quarantined by your organisation" : "Disabled by your organisation";
    const reason = record.reason?.trim().replace(/[.\s]+$/, "");
    return reason ? `${what}: ${reason}.` : `${what}.`;
}
/** A short, stable digest of the state (FNV-1a over the sorted records), for the heartbeat. */
export function agentsRevision(snapshot) {
    const text = [...snapshot.records]
        .sort((a, b) => a.specialistId.localeCompare(b.specialistId))
        .map((r) => `${r.specialistId}|${r.state}|${r.orderId}`)
        .join("\n");
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return `ar1-${h.toString(16).padStart(8, "0")}`;
}
/** What the heartbeat reports: the stopped specialists, how many of their sessions still run, and the orders applied. */
export const fleetAgentsReportSchema = z.object({
    revision: z.string().min(1).max(100),
    specialists: z
        .array(z.object({
        specialistId: z.string().min(1).max(200),
        entraAgentId: z.string().min(1).max(100).optional(),
        state: agentRegistryStateSchema,
        orderId: z.string().min(1).max(100),
        /** Sessions of this specialist still running on the machine (a disabled one finishing its turn). */
        running: z.number().int().nonnegative(),
    }))
        .max(500),
    /** The most recent order ids applied (enable included), newest last. */
    applied: z.array(z.string().min(1).max(100)).max(MAX_APPLIED_ORDERS),
});
/** The report for a snapshot, given how many sessions of each specialist key still run. */
export function agentsReport(snapshot, running) {
    return {
        revision: agentsRevision(snapshot),
        specialists: snapshot.records.map((r) => ({ specialistId: r.specialistId, ...(r.entraAgentId ? { entraAgentId: r.entraAgentId } : {}), state: r.state, orderId: r.orderId, running: running(r) })),
        applied: snapshot.applied.slice(-MAX_APPLIED_ORDERS),
    };
}
//# sourceMappingURL=agentRegistry.js.map